"use client";

import { c as _c } from "react/compiler-runtime";
import { AppointmentChecklistComponent } from "../appointment-checklist";
import { patientHasVisitToday, shouldShowSidebarChecklist } from "../appointment-checklist-visibility";
import { AppointmentItem } from "../appointment-item";
import { getPatientSidebarAppointmentGroups } from "../appointment-ordering";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import CollapsibleText from "@/components/ui/collapsible-text";
import { useAppointmentClockOf } from "@/hooks/use-clinic-clock";
import { cx } from "@/lib/tailwind-utils";
import { EventStatus } from "@/types/enums/event";
import type { EventDetail } from "@/types/event";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
interface Props {
  patientId: string;
  onStatusChange: (event: EventDetail, status: EventStatus) => void;
  handleViewNote: (eventId?: string, noteId?: string) => void;
}
const groupHeadingClass = "mb-2 text-caption font-semibold uppercase tracking-wider text-foreground-secondary";
export default function AppointmentsSection(t0) {
  const $ = _c(39);
  const {
    patientId,
    onStatusChange,
    handleViewNote
  } = t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = eventQueryOptions.searchPatientEvents(patientId, "");
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!patientId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: events,
    isPending
  } = useQuery(t3);
  const clockOf = useAppointmentClockOf();
  let t4;
  if ($[5] !== clockOf || $[6] !== events) {
    t4 = getPatientSidebarAppointmentGroups(events, clockOf);
    $[5] = clockOf;
    $[6] = events;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    todayAppointments,
    upcomingAppointments,
    pastAppointments
  } = t4;
  let t5;
  if ($[8] !== todayAppointments || $[9] !== upcomingAppointments) {
    t5 = [...todayAppointments, ...upcomingAppointments];
    $[8] = todayAppointments;
    $[9] = upcomingAppointments;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const openAppointments = t5;
  let t6;
  if ($[11] !== clockOf || $[12] !== events) {
    t6 = patientHasVisitToday(events, clockOf);
    $[11] = clockOf;
    $[12] = events;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== clockOf || $[15] !== t6) {
    t7 = {
      hasVisitToday: t6,
      clockOf
    };
    $[14] = clockOf;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const laterDays = t7;
  if (isPending) {
    let t8;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <PatientSidebarSectionSkeleton />;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  let t8;
  if ($[18] !== handleViewNote || $[19] !== laterDays || $[20] !== onStatusChange || $[21] !== openAppointments) {
    t8 = (title, appointments) => appointments.length > 0 ? <><h4 className={cx("my-2", groupHeadingClass)}>{title}</h4><div className="space-y-4">{appointments.map(appointment => <div key={appointment.id}><AppointmentItem appointment={appointment} showDetailedTime={true} showEdit={true} showStatusDropdown={true} onStatusChange={onStatusChange} />{shouldShowSidebarChecklist(appointment, openAppointments, laterDays) && appointment.status !== EventStatus.CANCELLED && <CollapsibleText text="Show checklist" collapsedText="Hide checklist" className="text-caption"><AppointmentChecklistComponent event={appointment} handleViewNote={handleViewNote} /></CollapsibleText>}</div>)}</div></> : null;
    $[18] = handleViewNote;
    $[19] = laterDays;
    $[20] = onStatusChange;
    $[21] = openAppointments;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const renderOpenGroup = t8;
  let t9;
  if ($[23] !== renderOpenGroup || $[24] !== todayAppointments) {
    t9 = renderOpenGroup("Today", todayAppointments);
    $[23] = renderOpenGroup;
    $[24] = todayAppointments;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== renderOpenGroup || $[27] !== upcomingAppointments) {
    t10 = renderOpenGroup("Upcoming Appointments", upcomingAppointments);
    $[26] = renderOpenGroup;
    $[27] = upcomingAppointments;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== onStatusChange || $[30] !== pastAppointments) {
    t11 = pastAppointments.length > 0 ? <><h4 className={cx("mt-4", groupHeadingClass)}>Past Appointments</h4><div className="space-y-4">{pastAppointments.map(appointment_0 => <div key={appointment_0.id}><AppointmentItem appointment={appointment_0} showEdit={true} showStatusDropdown={true} onStatusChange={onStatusChange} /></div>)}</div></> : null;
    $[29] = onStatusChange;
    $[30] = pastAppointments;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== openAppointments.length) {
    t12 = openAppointments.length === 0 && <p className="text-caption text-muted-foreground">No upcoming appointments</p>;
    $[32] = openAppointments.length;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t9) {
    t13 = <div>{t9}{t10}{t11}{t12}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}